/**
 * logBuffer.ts -- Console capture engine for the debug console panel.
 *
 * Monkey-patches console.log/warn/error/info at import time so every
 * app-created log is buffered in a capped ring buffer.  The original
 * console methods are called first so DevTools still work normally.
 *
 * React components subscribe via `useSyncExternalStore(subscribe, getSnapshot)`.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type LogLevel = "log" | "warn" | "error" | "info";

export interface LogEntry {
  id: number;
  level: LogLevel;
  /** Unix epoch ms */
  timestamp: number;
  /** Pre-formatted message string */
  message: string;
}

// ---------------------------------------------------------------------------
// Ring buffer
// ---------------------------------------------------------------------------

const MAX_ENTRIES = 500;

let entries: LogEntry[] = [];
let nextId = 1;
/** Identity token — changes on every mutation so React sees a new snapshot. */
let _revision = 0;

// ---------------------------------------------------------------------------
// Persistence (localStorage crash recovery)
//
// Buffer is flushed every 2 s while dirty and synchronously on beforeunload.
// On reload the captured entries are restored so pre-crash console output is
// visible in the debug console panel.
// ---------------------------------------------------------------------------

const LOG_BUFFER_STORAGE_KEY = "poseref-debug-log-buffer";

let dirty = false;

function flushLogBuffer(): void {
  try {
    localStorage.setItem(
      LOG_BUFFER_STORAGE_KEY,
      JSON.stringify({ entries, nextId }),
    );
    dirty = false;
  } catch {
    /* quota or private browsing */
  }
}

function loadLogBuffer(): void {
  try {
    const raw = localStorage.getItem(LOG_BUFFER_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as { entries: LogEntry[]; nextId: number };
    if (!parsed || !Array.isArray(parsed.entries)) return;
    entries = parsed.entries;
    snapshot = entries;
    if (typeof parsed.nextId === "number") {
      nextId = parsed.nextId;
    } else {
      let maxId = 0;
      for (const e of entries) if (e.id > maxId) maxId = e.id;
      nextId = maxId + 1;
    }
  } catch {
    /* malformed data — start fresh */
  }
}

loadLogBuffer();
setInterval(() => {
  if (dirty) flushLogBuffer();
}, 2000);
window.addEventListener("beforeunload", flushLogBuffer);

// ---------------------------------------------------------------------------
// Subscriber management (for useSyncExternalStore)
// ---------------------------------------------------------------------------

type Listener = () => void;
const listeners = new Set<Listener>();

function notify(): void {
  dirty = true;
  _revision++;
  for (const fn of listeners) fn();
}

export function subscribe(cb: Listener): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Snapshot reference — replaced on every mutation for useSyncExternalStore. */
let snapshot: readonly LogEntry[] = entries;

export function getSnapshot(): readonly LogEntry[] {
  return snapshot;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function clearAll(): void {
  entries = [];
  snapshot = entries;
  notify();
  flushLogBuffer(); // persist cleared state immediately
}

// ---------------------------------------------------------------------------
// Arg formatting
// ---------------------------------------------------------------------------

function formatArg(a: unknown): string {
  if (typeof a === "string") return a;
  if (a === undefined) return "undefined";
  if (a === null) return "null";
  try {
    return JSON.stringify(a);
  } catch {
    return String(a);
  }
}

function formatArgs(args: unknown[]): string {
  return args.map(formatArg).join(" ");
}

// ---------------------------------------------------------------------------
// Console patching
// ---------------------------------------------------------------------------

const originalLog = console.log;
const originalWarn = console.warn;
const originalError = console.error;
const originalInfo = console.info;

function push(level: LogLevel, args: unknown[]): void {
  const entry: LogEntry = {
    id: nextId++,
    level,
    timestamp: Date.now(),
    message: formatArgs(args),
  };

  if (entries.length >= MAX_ENTRIES) {
    entries = entries.slice(entries.length - MAX_ENTRIES + 1);
  }
  entries.push(entry);
  snapshot = entries;
  notify();
}

function installPatches(): void {
  console.log = (...args: unknown[]) => {
    originalLog.apply(console, args);
    push("log", args);
  };
  console.warn = (...args: unknown[]) => {
    originalWarn.apply(console, args);
    push("warn", args);
  };
  console.error = (...args: unknown[]) => {
    originalError.apply(console, args);
    push("error", args);
  };
  console.info = (...args: unknown[]) => {
    originalInfo.apply(console, args);
    push("info", args);
  };
}

// Auto-install on import
installPatches();
